/**
 * CoveragePolicyPage -- FI risk-console editor for the
 * TEMPO policy domain on the tenant policy-engine chassis
 * (overdraft-coverage arc OC-3, decision D7).
 *
 * Route: /tempo. Reachable only when the tenant carries
 * Institution.enableOverdraftPreview (nav gate in PortalLayout; the API
 * surface 404s server-side when the flag is off).
 *
 * Versioning model: PUT never edits in place -- it publishes a new
 * IMMUTABLE version (monotonic policyVersion). The form therefore carries
 * an explicit two-step publish affordance, and the Version History tab
 * lists every version ever published. Semantic-validation rejections
 * (422 POLICY_SEMANTIC_VALIDATION_FAILED) render with their reason codes;
 * shape rejections from the spec validator (400) render the same way.
 *
 * Spec 0.6.0 adds two cure-cap dials (GH #2230): `cureCapBasis` names which
 * net-flow statistic `maxCureDays` multiplies, and `cureCapHaircut` removes a
 * stated fraction of the resulting cap. Both are OPTIONAL on
 * LiquidityPolicyParameters and both round-trip through this form: a version
 * that carried no basis publishes with no basis key, which the spec's absence
 * rule reads as NET_FLOW_MEDIAN_ALL_DAYS, so a publish from this screen cannot
 * silently move a tenant off the statistic its prior version used.
 *
 * GH #2920 extends that round-trip to the four remaining OPTIONAL properties.
 * `dataLane` is an editable select whose "Not set" option publishes no key.
 * `workoutOffer`, `lineTerms` and `standbyTargeting` are held as the objects
 * the effective version carried and republished by reference, so editing alpha
 * cannot drop them. The screen renders those three read-only; editing them is
 * not built here.
 *
 * CHROME PLACEMENT (GH #2924). The level-1 heading and the subtitle render in
 * the loading, error and loaded states. The skeleton, the load-failure alert,
 * the no-version banner and the tabbed editor are siblings below them, per KG
 * `technology-innovation/ui-package/2026-05-18-canonical-chrome-renders-outside-error-conditionals.md`.
 * The AppHeader chrome title reads "Coverage Policy" on this route while the
 * read is pending, so a body carrying no heading during the skeleton reports to
 * a DOM-reading proof as a satisfied wait.
 *
 * READ OUTCOME (GH #2926, the GH #2913 class). Everything below the chrome
 * renders from one outcome, QueryReadOutcome via monitoring/read-state.ts:
 *
 *   loading -> skeleton
 *   failed  -> the load-failure alert alone; no banner, no tabs, no editor
 *   unread  -> a not-yet-read notice alone. TanStack v5 reports a PAUSED
 *              first fetch (offline at mount) as pending with isFetching
 *              false, so isLoading is false with no data and no error.
 *   read    -> the no-version banner when the policy is null, then the tabs
 *
 * Before this, the banner was gated on `!isLoading && !policy && !loadError`
 * and the tabs on `!isLoading`. A paused first fetch therefore printed "No
 * Aaim Tempo coverage policy has been published" with the baseline dials and
 * a live publish action under it, and a failed read rendered the editor
 * beside the alert. The 404-on-OFF path is unchanged: the hook maps that
 * status to a null policy, which is a read, so the banner states it.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  Checkbox,
  Heading,
  Input,
  Select,
  Skeleton,
  Badge,
  TableLink,
  Tabs,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/tempo';
import {
  PUBLISHED_FOR_OTHER_TENANT,
  useEffectiveLiquidityPolicy,
  usePublishLiquidityPolicy,
  useSetTempoDeliveryMode,
  useTempoDeliveryMode,
  type LiquidityCureCapBasis,
  type LiquidityDataLaneMode,
  type LiquidityPolicyParameters,
  type MemberLiquidityPolicyInput,
} from '../../hooks/useTempoPolicy';
import {
  DELIVERY_MODE_DESCRIPTION,
  DELIVERY_MODE_LABEL,
  DELIVERY_MODES,
} from './delivery-mode';
import { DATA_LANE_LABEL, FIELDS, PRICING_MODE_LABEL } from './coverage-policy/labels';
import { PolicyVersionHistoryTab } from './PolicyVersionHistoryTab';
import { LimitsDistributionPanel } from './LimitsDistributionPanel';
import { readOutcome, readTime, refreshFailed } from '../monitoring/read-state';

/**
 * Numeric dials are held as STRINGS while editing (so a half-typed value
 * never snaps), then parsed once at publish time. Field keys mirror
 * LiquidityPolicyParameters paths. Every dial except the graduated-line
 * multiplier is required: the generated types mark spec-defaulted
 * properties non-optional (openapi-typescript defaultNonNullable), and the
 * form pre-fills the spec defaults so the requirement costs no keystrokes.
 */
const REQUIRED_NUMERIC_FIELDS = [
  'alpha',
  'maxCureDays',
  'entryCap',
  'standardCap',
  'minTenureDays',
  'minRecurringInflowScore',
  'chronicUseEventThreshold12mo',
  'feeToInflowBreakerRatio',
  'stepDownDecrementRatio',
  'coolingOffDays',
  'minDataCompletenessRatio',
] as const;

const SPEC_DEFAULT_FORM = {
  alpha: '0.15',
  maxCureDays: '30',
  entryCap: '500',
  standardCap: '1500',
  graduatedLineMultiplier: '',
  programCapUsd: '',
  cureCapHaircut: '',
  minTenureDays: '60',
  minRecurringInflowScore: '0.5',
  chronicUseEventThreshold12mo: '6',
  feeToInflowBreakerRatio: '0.02',
  stepDownDecrementRatio: '0.25',
  coolingOffDays: '90',
  minDataCompletenessRatio: '0.8',
};

/**
 * Which figure each basis reads, in the words an institution's staff use on
 * this page. Authored here for that reader (GH #678, operator ruling Q3 of
 * 2026-10-23), not rendered from the LiquidityCureCapBasis description in
 * tempo.openapi.yaml; the meaning is the contract's, the wording is the page's.
 *
 * Declared as a Record over the generated union, so a member added to the spec
 * enum fails this file at compile time instead of silently rendering an option
 * the FI never sees. The Select's options are derived from these keys for the
 * same reason, and in the spec's declaration order.
 */
const CURE_CAP_BASIS_STATISTIC: Record<LiquidityCureCapBasis, string> = {
  NET_FLOW_MEDIAN_ALL_DAYS:
    'the median daily net inflow over all 30 days, counting a day with no posted transaction as $0',
  NET_FLOW_MEDIAN_POSITIVE_DAYS:
    'the median daily net inflow over only the days that netted more than $0',
};

/**
 * The enum members are rendered VERBATIM as the option labels. They are the
 * value an examiner reads back off the recommendation row and off the what-if
 * distribution, so the dial the FI picks and the fact the engine records read
 * as the same string. The readable sentence is the helper text below the field.
 *
 * The cast is over Object.keys, whose return type is `string[]`; the Record
 * above is what guarantees the key set is exactly the generated union.
 */
const CURE_CAP_BASIS_OPTIONS = (Object.keys(CURE_CAP_BASIS_STATISTIC) as LiquidityCureCapBasis[]).map(
  (basis) => ({ value: basis, label: basis }),
);

/**
 * The statistic sentence for whichever basis the form currently holds. An
 * UNSET basis reads as the all-days median because that is the spec's declared
 * absence rule for this property, and stating it is what keeps the screen from
 * showing a blank where an FI would otherwise guess. The direction is the
 * OPPOSITE of `dataLane`, whose absence leaves the lane unresolved.
 */
function cureCapBasisStatistic(basis: LiquidityCureCapBasis | ''): string {
  return basis === ''
    ? CURE_CAP_BASIS_STATISTIC.NET_FLOW_MEDIAN_ALL_DAYS
    : CURE_CAP_BASIS_STATISTIC[basis];
}

/**
 * GH #2920 dials. LiquidityDataLaneMode is the generated enum re-exported by
 * useTempoPolicy beside LiquidityCureCapBasis; CarriedDials is derived from the
 * generated LiquidityPolicyParameters, so a spec edit to the lane enum or to
 * the property set reaches this screen through the type checker.
 */
type CarriedDials = Pick<components['schemas']['LiquidityPolicyParameters'], 'workoutOffer' | 'lineTerms' | 'standbyTargeting'>;

/**
 * What each lane means to the institution, as a fragment that follows the
 * lane's readable name from labels.ts. Authored for the institution's staff
 * (GH #678, operator ruling Q3 of 2026-10-23); the contract's description of
 * LiquidityDataLaneMode is not rendered. Declared as a Record over the
 * generated union so a member added to the enum fails this file at compile
 * time; the Select options are derived from these keys, in the spec's
 * declaration order.
 */
const DATA_LANE_MEANING: Record<LiquidityDataLaneMode, string> = {
  CORE_ONLY: 'every score uses only records your core reported, so you can verify a core-only statement',
  CORE_PLUS_AGGREGATED:
    'scores add aggregated account records only where the account holder has already consented',
};

/**
 * Said when no lane is selected. An absent lane stays absent on publish (GH
 * #2920), and the contract treats a version with none as a configuration
 * error rather than falling back to the more permissive lane.
 */
const DATA_LANE_UNSET_NOTE =
  'No data source is set on this version, and Aaim treats a version with none as a configuration error.';

/**
 * Enum members render VERBATIM as option labels, matching the cure-cap select:
 * the value an examiner reads off the provenance record and the value the FI
 * picks here are the same string.
 */
const DATA_LANE_OPTIONS = (Object.keys(DATA_LANE_MEANING) as LiquidityDataLaneMode[]).map(
  (lane) => ({ value: lane, label: lane }),
);

/**
 * What each carried setting is, for the institution's staff (GH #678, operator
 * ruling Q3 of 2026-10-23). Authored here; the contract's descriptions of
 * WorkoutOfferRules, LineTermsEnvelope and StandbyLineTargeting are not
 * rendered. A Record over the property set, so a fourth carried property
 * cannot be added without its text.
 */
const CARRIED_DIAL_DESCRIPTION: Record<keyof CarriedDials, string> = {
  workoutOffer:
    'Your rules for the workout offer made to a member whose coverage balance has gone negative: when the offer and the first cure-window letter go out, when fees stop, and which balances qualify. Your institution sets every value, and Aaim supplies no default you have not written.',
  lineTerms:
    'The pricing range your institution allows on the Tier 2 lines it arranges: APR range, term, minimum payment and pricing structure. Your institution writes every value, and Aaim makes no pricing or credit decision from them.',
  standbyTargeting:
    "Who is invited to the standby line. Nothing starts without a member application, so these settings decide only who is invited. Aaim rechecks each member's cure-time trend every night, so the invited group changes as members' patterns change.",
};

/** Scalars print as themselves; a band, an array or a descriptor list prints as its JSON. */
function formatDialValue(value: unknown): string {
  return typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);
}

/**
 * One carried dial, read-only. The label is the spec symbol, so the field an
 * operator reads here is the field an examiner reads off the version record.
 * Editing these three is not built in this screen (GH #2920).
 */
function CarriedDialBlock({
  property,
  value,
}: {
  property: keyof CarriedDials;
  value: object | undefined;
}) {
  const entries = value ? Object.entries(value).filter(([, held]) => held !== undefined) : [];
  // Absence and an empty object are two different values the version can
  // carry, and the publish republishes whichever it holds (the schema declares
  // no required field on these objects, so {} is valid). The sentence says
  // which one the version holds.
  const emptyState =
    value === undefined
      ? 'The effective version carries no value for this property.'
      : 'Carried as an empty object.';
  return (
    <div>
      <BodyText variant="ui" className="text-sm text-primary font-mono">
        {property}
      </BodyText>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        {CARRIED_DIAL_DESCRIPTION[property]}
      </BodyText>
      {entries.length === 0 ? (
        <BodyText variant="ui" className="text-sm text-secondary mt-2">
          {emptyState}
        </BodyText>
      ) : (
        <dl className="mt-2 space-y-1">
          {entries.map(([field, held]) => (
            <div key={field} className="flex flex-wrap gap-2 text-sm">
              <dt className="text-tertiary font-mono">{field}</dt>
              <dd className="text-primary font-mono break-all">{formatDialValue(held)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function parametersToForm(parameters: LiquidityPolicyParameters): typeof SPEC_DEFAULT_FORM {
  const asField = (value: number | undefined): string => (value === undefined ? '' : String(value));
  return {
    alpha: asField(parameters.alpha),
    maxCureDays: asField(parameters.maxCureDays),
    entryCap: asField(parameters.tierCaps.entryCap),
    standardCap: asField(parameters.tierCaps.standardCap),
    graduatedLineMultiplier: asField(parameters.tierCaps.graduatedLineMultiplier),
    programCapUsd: asField(parameters.programCapUsd),
    cureCapHaircut: asField(parameters.cureCapHaircut),
    minTenureDays: asField(parameters.eligibility?.minTenureDays),
    minRecurringInflowScore: asField(parameters.eligibility?.minRecurringInflowScore),
    chronicUseEventThreshold12mo: asField(parameters.guardrails.chronicUseEventThreshold12mo),
    feeToInflowBreakerRatio: asField(parameters.guardrails.feeToInflowBreakerRatio),
    stepDownDecrementRatio: asField(parameters.guardrails.stepDownDecrementRatio),
    coolingOffDays: asField(parameters.guardrails.coolingOffDays),
    minDataCompletenessRatio: asField(parameters.guardrails.minDataCompletenessRatio),
  };
}

/**
 * The three nested dials this screen does not edit, held by reference from the
 * effective version. A key the version omits is omitted here, so the spread in
 * buildInput republishes the exact property set that was loaded.
 *
 * Sibling of parametersToForm and called from the same effect: parametersToForm
 * returns the numeric-string bag setField writes, which cannot hold an object.
 */
function parametersToCarriedDials(parameters: LiquidityPolicyParameters): CarriedDials {
  return {
    ...(parameters.workoutOffer !== undefined ? { workoutOffer: parameters.workoutOffer } : {}),
    ...(parameters.lineTerms !== undefined ? { lineTerms: parameters.lineTerms } : {}),
    ...(parameters.standbyTargeting !== undefined
      ? { standbyTargeting: parameters.standbyTargeting }
      : {}),
  };
}

/**
 * GH #2929 deploy-order guard. tempo 0.19.0 removes FEE_FREE_GRACE and
 * SINGLE_APR_LINE from pricingMode; tempo-engine
 * scripts/migrate-pricing-mode-tiers.ts rewrites stored versions that carry
 * them. Until that script has run, this page can load a version whose stored
 * value matches no option in the select, and republishing it is refused with
 * 400. This applies the script's mapping
 * (tempo-engine src/policy-chassis/pricing-mode-migration.ts) to the loaded
 * document, so the page publishes a 0.19.0 posture in either order. After the
 * script has run it matches nothing.
 */
export function loadedPricingPosture(parameters: LiquidityPolicyParameters): {
  pricingMode: LiquidityPolicyParameters['pricingMode'];
  carriedDials: CarriedDials;
} {
  const carriedDials = parametersToCarriedDials(parameters);
  const stored: string = parameters.pricingMode;
  if (stored === 'FEE_FREE_GRACE') {
    return { pricingMode: 'NO_PER_ITEM_FEE', carriedDials };
  }
  if (stored === 'SINGLE_APR_LINE') {
    return {
      pricingMode: 'NO_PER_ITEM_FEE',
      carriedDials: {
        ...carriedDials,
        lineTerms: { ...(carriedDials.lineTerms ?? {}), pricingStructure: 'SINGLE_APR_LINE' },
      },
    };
  }
  return { pricingMode: parameters.pricingMode, carriedDials };
}

export function CoveragePolicyPage() {
  const [activeTab, setActiveTab] = useState('parameters');
  const policyQuery = useEffectiveLiquidityPolicy();
  const { data: policy, error: loadError } = policyQuery;
  const outcome = readOutcome(policyQuery);
  const publishMutation = usePublishLiquidityPolicy();
  // GH #2744: the LIVE switch, read and written separately from the dials.
  const { data: liveMode, isLoading: modeLoading } = useTempoDeliveryMode();
  const setModeMutation = useSetTempoDeliveryMode();

  const [form, setForm] = useState(SPEC_DEFAULT_FORM);
  const [pricingMode, setPricingMode] =
    useState<LiquidityPolicyParameters['pricingMode']>('PER_ITEM_FEE');
  // '' is the ABSENT basis, not a third enum member: the empty state is what a
  // pre-0.6.0 version carries, and buildInput omits the key for it so the
  // publish reproduces what was loaded. Held beside pricingMode rather than in
  // `form` because `form` is the numeric-string bag setField writes.
  const [cureCapBasis, setCureCapBasis] = useState<LiquidityCureCapBasis | ''>('');
  // GH #2920. '' is the ABSENT lane and publishes no key, on the same rule as
  // cureCapBasis above. The two absences mean different things to the engine:
  // an absent basis reads as all 30 days, an absent lane is unresolved.
  const [dataLane, setDataLane] = useState<LiquidityDataLaneMode | ''>('');
  // The three dials this screen carries and does not edit; held by reference.
  const [carriedDials, setCarriedDials] = useState<CarriedDials>({});
  const [excludePriorChargeOff, setExcludePriorChargeOff] = useState(true);
  const [requireRegEOptIn, setRequireRegEOptIn] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  // GH #2939. The tenant whose version last hydrated the dials; undefined while
  // the dials are the platform baseline.
  const [dialsTenantId, setDialsTenantId] = useState<string | undefined>(undefined);

  // Hydrate the form from the effective version once it loads (and after
  // each successful publish, via the query-cache update).
  useEffect(() => {
    if (policy) {
      setForm(parametersToForm(policy.parameters));
      const posture = loadedPricingPosture(policy.parameters);
      setPricingMode(posture.pricingMode);
      setCureCapBasis(policy.parameters.cureCapBasis ?? '');
      setDataLane(policy.parameters.dataLane ?? '');
      setCarriedDials(posture.carriedDials);
      setExcludePriorChargeOff(policy.parameters.eligibility?.excludePriorChargeOff ?? true);
      setRequireRegEOptIn(policy.parameters.eligibility?.requireRegEOptInForFeeCoverage ?? true);
      setDialsTenantId(policy.tenantId);
    } else if (policy === null) {
      // A read that finds no version puts the baseline back, so dials hydrated
      // by an earlier read, possibly another institution's, never publish as
      // this institution's first version under the "no policy published" banner.
      setForm(SPEC_DEFAULT_FORM);
      setPricingMode('PER_ITEM_FEE');
      setCureCapBasis('');
      setDataLane('');
      setCarriedDials({});
      setExcludePriorChargeOff(true);
      setRequireRegEOptIn(true);
      setDialsTenantId(undefined);
    }
  }, [policy]);

  const setField = (key: keyof typeof SPEC_DEFAULT_FORM) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: event.target.value }));
      setConfirming(false);
      setPublishedVersion(null);
    };

  // Every input buildInput reads, so a candidate preview can tell it no longer
  // describes the form (GH #2747). The form values are the raw strings, so a
  // re-hydration that rewrites "0.20" as "0.2" also clears a candidate result:
  // it can hide a result that still holds, never show one that does not.
  const dialsKey = JSON.stringify([
    form,
    pricingMode,
    cureCapBasis,
    dataLane,
    carriedDials,
    excludePriorChargeOff,
    requireRegEOptIn,
  ]);

  const missingRequired = useMemo(
    () =>
      REQUIRED_NUMERIC_FIELDS.filter(
        (key) => form[key].trim() === '' || Number.isNaN(Number(form[key])),
      ),
    [form],
  );

  function buildInput(): MemberLiquidityPolicyInput | null {
    if (missingRequired.length > 0) {
      setLocalError(`Enter a numeric value for: ${missingRequired.join(', ')}`);
      return null;
    }
    setLocalError(null);
    const optional = (raw: string): number | undefined =>
      raw.trim() === '' || Number.isNaN(Number(raw)) ? undefined : Number(raw);
    const parameters: LiquidityPolicyParameters = {
      alpha: Number(form.alpha),
      maxCureDays: Number(form.maxCureDays),
      tierCaps: {
        entryCap: Number(form.entryCap),
        standardCap: Number(form.standardCap),
        ...(optional(form.graduatedLineMultiplier) !== undefined
          ? { graduatedLineMultiplier: optional(form.graduatedLineMultiplier) }
          : {}),
      },
      // Spec v0.3.1: optional program-wide ceiling; omitted means the
      // standard tier cap remains the effective program ceiling.
      ...(optional(form.programCapUsd) !== undefined
        ? { programCapUsd: optional(form.programCapUsd) }
        : {}),
      // Spec 0.6.0, GH #2230. Both keys are OMITTED when unset, and that
      // matters in opposite directions. An omitted basis is read by the spec's
      // absence rule as NET_FLOW_MEDIAN_ALL_DAYS, so a version loaded without
      // one republishes on the statistic it already used; sending a literal
      // ALL_DAYS instead would be arithmetically identical but would record a
      // choice the FI never made. An omitted haircut removes nothing.
      ...(cureCapBasis !== '' ? { cureCapBasis } : {}),
      ...(optional(form.cureCapHaircut) !== undefined
        ? { cureCapHaircut: optional(form.cureCapHaircut) }
        : {}),
      // GH #2920. The lane key is omitted when no lane is selected, so a
      // version published before spec 0.4.0 republishes without acquiring one.
      ...(dataLane !== '' ? { dataLane } : {}),
      // The three dials the screen does not edit, spread by reference from the
      // effective version. carriedDials holds only the keys that version
      // carried, so the publish reproduces its property set exactly. Before
      // this spread, every publish rebuilt `parameters` from the form field
      // list and dropped all three (GH #2920).
      ...carriedDials,
      eligibility: {
        minTenureDays: Number(form.minTenureDays),
        minRecurringInflowScore: Number(form.minRecurringInflowScore),
        excludePriorChargeOff,
        requireRegEOptInForFeeCoverage: requireRegEOptIn,
      },
      guardrails: {
        chronicUseEventThreshold12mo: Number(form.chronicUseEventThreshold12mo),
        feeToInflowBreakerRatio: Number(form.feeToInflowBreakerRatio),
        stepDownDecrementRatio: Number(form.stepDownDecrementRatio),
        coolingOffDays: Number(form.coolingOffDays),
        minDataCompletenessRatio: Number(form.minDataCompletenessRatio),
      },
      pricingMode,
    };
    // The published version's stamp is SERVER-DERIVED from
    // Institution.tempoDeliveryMode at publish time; the write shape carries no
    // deliveryMode at all. It used to send `liveMode?.deliveryMode ?? 'OFF'`,
    // which made the audited party the author of its own audit stamp AND wrote
    // a literal OFF onto immutable examiner evidence whenever the switch merely
    // could not be READ. Publishing is blocked in that state instead -- see the
    // modeUnreadable guard on the publish action.
    return { parameters };
  }

  function handlePublishClick() {
    const input = buildInput();
    if (!input) return;
    setConfirming(true);
  }

  function handleConfirmPublish() {
    const input = buildInput();
    if (!input) return;
    setConfirming(false);
    publishMutation.mutate(
      {
        input,
        // GH #2939. The tenant this page loaded, from the server's own
        // delivery-mode response. The hook re-reads the mode before the PUT and
        // refuses when the session now resolves to another institution; a null
        // mode is refused there too, and the refusal renders in the publish
        // error alert below.
        expectedTenantId: liveMode?.tenantId ?? null,
        // The tenant whose version hydrated the dials. Absent when no version
        // exists and the dials are the platform baseline.
        dialsTenantId,
      },
      {
        onSuccess: (published) => setPublishedVersion(published.policyVersion),
      },
    );
  }

  const publishError = publishMutation.error;
  // Distinct from "the mode is OFF": the switch could not be READ. The publish
  // action and the mode buttons both key off !liveMode (which also covers the
  // loading window); this flag drives only the operator-facing explanation, so
  // the message does not flash during a normal load.
  const modeUnreadable = !modeLoading && !liveMode;

  // GH #2926 review: this section reads its own endpoint and this page is its
  // only screen, so it renders beside a failed policy read as well as inside
  // the editor. Held in one constant so both placements are the same control.
  //
  // GH #2744: the delivery mode is set HERE and takes effect immediately.
  // It is not part of the publish below, and that separation is the point
  // -- the retired "Program enabled" switch rode along with the dials, so
  // an FI could only turn the program off by publishing a version, and
  // every dial revision silently re-asserted the on/off state.
  //
  // Three buttons rather than a toggle because the state is not binary,
  // and a two-position control cannot express the middle one.
  const deliveryModeSection = (
    <div className="pt-4 border-t border-recessed">
      <BodyText variant="ui" className="text-sm text-primary font-medium">
        Delivery mode
      </BodyText>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        Applies immediately to your institution. Separate from publishing dials;
        each published version records the mode in force when it was published.
      </BodyText>
      {setModeMutation.isError && (
        <Alert type="error" title="Delivery mode not changed" className="mt-3">
          {setModeMutation.error.message}
        </Alert>
      )}
      {!modeLoading && !liveMode && (
        <Alert type="warning" title="Delivery mode unavailable" className="mt-3">
          The current mode could not be read, so it is not shown and cannot be
          changed here. This is not the same as the program being off.
        </Alert>
      )}
      <div className="flex flex-wrap items-stretch gap-3 mt-3">
        {DELIVERY_MODES.map((mode) => {
          const isCurrent = liveMode?.deliveryMode === mode;
          return (
            <Button
              key={mode}
              variant={isCurrent ? 'primary' : 'secondary'}
              // The tenantId comes from the server's own response, never from
              // the session: a platform-admin session carries no tenantId and
              // resolves to apex, so guessing here would target the wrong
              // institution while the screen showed this one.
              disabled={!liveMode || isCurrent || setModeMutation.isPending}
              onClick={() => {
                if (!liveMode) return;
                setModeMutation.mutate({
                  tenantId: liveMode.tenantId,
                  deliveryMode: mode,
                });
              }}
            >
              {DELIVERY_MODE_LABEL[mode]}
            </Button>
          );
        })}
      </div>
      {/*
        The description is rendered ONLY when a mode was actually read.
        It used to fall back to DELIVERY_MODE_DESCRIPTION.OFF, so the
        "could not be read" warning above sat directly on top of the
        sentence "No scoring runs. Every account records no coverage." --
        two contradictory claims about the same switch, and the confident
        one was the invented one.
      */}
      {liveMode && (
        <BodyText variant="ui" className="text-xs text-tertiary mt-2">
          {DELIVERY_MODE_DESCRIPTION[liveMode.deliveryMode]}
        </BodyText>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          {/* Level 2: PortalLayout's AppHeader renders the page h1 (operator ruling on GH #2664, 2026-08-20). */}
          <Heading level={2} size="page">Coverage Policy</Heading>
          <BodyText variant="ui" className="text-tertiary text-sm mt-1">
            Overdraft coverage dials for your institution. Publishing creates an immutable
            version on the policy chassis; your institution makes every pay/no-pay and credit decision.
          </BodyText>
          {/* mlv2-console D2, the C3 two-path half (sprint-queue/
              sprint-member-liquidity-v2/GO-LIVE-PLAN.md Section 13.2(b)):
              the dials on this page are institution-wide; the per-account
              screen is where one member's features and reason codes are read.
              A TableLink rather than a navigate(): this component is rendered
              by a suite that mounts it outside a Router, and a react-router
              hook or <Link> here throws in that suite. */}
          <div className="mt-3">
            <TableLink href="/tempo/accounts">Look up an account</TableLink>
          </div>
        </div>
        {policy && (
          <div className="flex items-center gap-3 shrink-0">
            <Badge
              variant={liveMode && liveMode.deliveryMode !== 'OFF' ? 'success' : 'neutral'}
              size="small"
            >
              {modeLoading
                ? 'Loading'
                : liveMode
                  ? DELIVERY_MODE_LABEL[liveMode.deliveryMode]
                  : 'Unknown'}
            </Badge>
            <div className="text-right">
              <BodyText variant="ui" className="text-sm text-primary font-mono">
                Version {policy.policyVersion}
              </BodyText>
              <BodyText variant="ui" className="text-xs text-tertiary">
                Effective {formatDateTime(policy.effectiveAt)}
              </BodyText>
            </div>
          </div>
        )}
      </div>

      {outcome === 'loading' && (
        <div className="space-y-4" data-testid="coverage-policy-loading">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {outcome === 'failed' && (
        <Alert type="error" title="Policy load failed">
          <div className="space-y-1">
            <div>{loadError?.message}</div>
            <div>No version was read, so this page shows no dials and no publish action.</div>
          </div>
        </Alert>
      )}

      {/* The mode switch does not depend on the policy read. */}
      {outcome === 'failed' && deliveryModeSection}

      {outcome === 'unread' && (
        <div className="p-4 rounded-sm bg-accent/5 border border-recessed" role="status">
          <BodyText variant="ui" className="text-sm text-secondary">
            The coverage policy has not been read yet. This page shows no version, no dials and no
            publish action until the read completes.
          </BodyText>
        </div>
      )}

      {/* A background refetch rejected over a successful read. The version and
          dials below are that earlier read, so the editor stays and the notice
          says when it was taken. */}
      {refreshFailed(policyQuery) && (
        <Alert type="warning" title="Policy refresh failed">
          {`The version and dials shown are from the read at ${readTime(policyQuery.dataUpdatedAt)}.${loadError ? ` The latest refresh failed: ${loadError.message}` : ''}`}
        </Alert>
      )}

      {/* `read` with a null policy is the measured absence: the hook maps the
          404 to null. Every other outcome says nothing about what this
          institution has published, and the editor below would read as the
          baseline dials in force. */}
      {outcome === 'read' && !policy && (
        <div className="p-4 rounded-sm bg-accent/5 border border-recessed">
          <BodyText variant="ui" className="text-sm text-secondary">
            No Aaim Tempo coverage policy has been published for your institution yet. The values
            below are the platform baseline dials; publishing creates version 1.
          </BodyText>
        </div>
      )}

      {outcome === 'read' && (
        <Tabs
          tabs={[
            { id: 'parameters', label: 'Parameters' },
            { id: 'history', label: 'Version History' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          // Version History fetches the version list when it mounts, so arrow
          // keys only move focus and Enter or Space selects.
          activation="manual"
        />
      )}

      {outcome === 'read' && activeTab === 'history' && (
        <PolicyVersionHistoryTab domain="TEMPO" />
      )}

      {outcome === 'read' && activeTab !== 'history' && (
        <div className="space-y-6">
          <Card className="p-6">
            <Heading level={2} size="card" className="font-sans mb-4">Limit Function</Heading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Alpha (inflow fraction)"
                type="number"
                min={0.05}
                max={0.25}
                step={0.01}
                value={form.alpha}
                onChange={setField('alpha')}
                helperText="Fraction of reliable monthly inflow used for the base limit (0.05-0.25)"
              />
              <Input
                label="Max cure days"
                type="number"
                min={7}
                max={60}
                step={1}
                value={form.maxCureDays}
                onChange={setField('maxCureDays')}
                // The prior copy read "days of median positive net flow",
                // naming a statistic the engine did not compute: through spec
                // 0.5.0 the cap multiplied the all-days median. The basis is
                // now a dial, so the sentence states the one this version
                // selects rather than asserting either.
                helperText={`Days of the cure-cap basis the limit must be curable within (7-60). This version multiplies ${cureCapBasisStatistic(cureCapBasis)}.`}
              />
              <Select
                label="Cure-cap basis"
                value={cureCapBasis}
                onChange={(e) => {
                  // The generated union is the option value set, so the cast
                  // narrows a DOM string back to it. The empty placeholder
                  // option is rendered disabled by the Select primitive, so no
                  // '' can arrive here.
                  setCureCapBasis(e.target.value as LiquidityCureCapBasis);
                  setConfirming(false);
                  setPublishedVersion(null);
                }}
                options={CURE_CAP_BASIS_OPTIONS}
                placeholder="Not set -- reads the all-days median"
                helperText={`Which net-flow statistic max cure days multiplies: ${cureCapBasisStatistic(cureCapBasis)}. Publishing with a basis selected creates a new version; no version is edited in place. Read the result beside netFlowPositiveDayCount30d on the account screen, which says how many days the positive-days median was taken over.`}
              />
              <Select
                label={FIELDS.pricingMode.label}
                value={pricingMode}
                onChange={(e) => {
                  setPricingMode(e.target.value as LiquidityPolicyParameters['pricingMode']);
                  setConfirming(false);
                }}
                options={[
                  { value: 'PER_ITEM_FEE', label: PRICING_MODE_LABEL.PER_ITEM_FEE },
                  { value: 'NO_PER_ITEM_FEE', label: PRICING_MODE_LABEL.NO_PER_ITEM_FEE },
                ]}
                helperText={FIELDS.pricingMode.helper}
              />
              <Input
                label="Tier 1 entry cap (USD)"
                type="number"
                min={0}
                step={25}
                value={form.entryCap}
                onChange={setField('entryCap')}
                helperText="Must not exceed the standard cap"
              />
              <Input
                label="Tier 1 standard cap (USD)"
                type="number"
                min={0}
                step={25}
                value={form.standardCap}
                onChange={setField('standardCap')}
              />
              <Input
                label="Graduated line multiplier"
                type="number"
                min={1.0}
                max={1.5}
                step={0.05}
                value={form.graduatedLineMultiplier}
                onChange={setField('graduatedLineMultiplier')}
                helperText="Tier 2 offer as a multiple of the standing Tier 1 limit (1.0-1.5; blank = platform default)"
              />
              <Input
                label="Program cap (USD)"
                type="number"
                min={0}
                step={25}
                value={form.programCapUsd}
                onChange={setField('programCapUsd')}
                helperText="Program-wide per-account ceiling; leave blank to let the standard tier cap remain the effective ceiling. Must be at least the entry cap when set."
              />
              <Input
                label="Cure-cap haircut"
                type="number"
                min={0}
                step={0.05}
                value={form.cureCapHaircut}
                onChange={setField('cureCapHaircut')}
                // No `max` attribute: the spec declares exclusiveMaximum 1 and
                // the HTML attribute is inclusive, so a hand-picked 0.99 here
                // would be a second bound that disagrees with the contract. The
                // spec validator rejects 1 or above and the rejection renders
                // in the Publish Alert below.
                helperText="Fraction of the cure cap removed before it binds, applied as (1 - haircut); 0.25 leaves three quarters standing. Below 1. Leave blank to remove nothing."
              />
            </div>
          </Card>

          <Card className="p-6">
            <Heading level={2} size="card" className="font-sans mb-4">Eligibility</Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Minimum tenure (days)"
                type="number"
                min={0}
                step={1}
                value={form.minTenureDays}
                onChange={setField('minTenureDays')}
              />
              <Input
                label="Minimum recurring-inflow score"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.minRecurringInflowScore}
                onChange={setField('minRecurringInflowScore')}
              />
              <Checkbox
                label="Exclude members with a prior charge-off"
                checked={excludePriorChargeOff}
                onChange={(checked) => {
                  setExcludePriorChargeOff(checked);
                  setConfirming(false);
                }}
              />
              <Checkbox
                label="Require Reg E opt-in for fee-bearing coverage"
                checked={requireRegEOptIn}
                onChange={(checked) => {
                  setRequireRegEOptIn(checked);
                  setConfirming(false);
                }}
              />
            </div>
          </Card>

          <Card className="p-6">
            <Heading level={2} size="card" className="font-sans mb-4">
              Sustainability Guardrails
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Chronic-use threshold (events / 12 mo)"
                type="number"
                min={1}
                step={1}
                value={form.chronicUseEventThreshold12mo}
                onChange={setField('chronicUseEventThreshold12mo')}
                helperText="At or above this count, the member is flagged for a graduation-or-off-ramp decision"
              />
              <Input
                label="Fee-to-inflow breaker ratio"
                type="number"
                min={0}
                max={0.5}
                step={0.005}
                value={form.feeToInflowBreakerRatio}
                onChange={setField('feeToInflowBreakerRatio')}
                helperText="Above this ratio, limit growth freezes (must be greater than 0, at most 0.5)"
              />
              <Input
                label="Step-down decrement ratio"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.stepDownDecrementRatio}
                onChange={setField('stepDownDecrementRatio')}
                helperText="Per-cycle limit reduction on deterioration -- step down, never cliff"
              />
              <Input
                label="Cooling-off period (days)"
                type="number"
                min={30}
                step={1}
                value={form.coolingOffDays}
                onChange={setField('coolingOffDays')}
                helperText="After charge-off or extended negative balance (minimum 30)"
              />
              <Input
                label="Minimum data-completeness ratio"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.minDataCompletenessRatio}
                onChange={setField('minDataCompletenessRatio')}
                helperText="Below this, limits scale down proportionally -- no data, no coverage"
              />
            </div>
          </Card>

          {/* GH #2920. The lane is the one of the four newly wired dials this
              screen edits. Options are supplied as children so the "Not set"
              entry is selectable: the Select primitive renders its own
              placeholder option disabled, and an FI that wants to publish a
              version with no lane needs a way back to the absent state. */}
          <Card className="p-6">
            <Heading level={2} size="card" className="font-sans mb-4">Data Lane</Heading>
            <div className="grid grid-cols-1 gap-4">
              <Select
                label="Data lane"
                value={dataLane}
                onChange={(e) => {
                  setDataLane(e.target.value as LiquidityDataLaneMode | '');
                  setConfirming(false);
                  setPublishedVersion(null);
                }}
                helperText={`${FIELDS.dataLane.helper ?? ''} ${
                  dataLane === ''
                    ? DATA_LANE_UNSET_NOTE
                    : `${DATA_LANE_LABEL[dataLane]}: ${DATA_LANE_MEANING[dataLane]}.`
                }`.trim()}
              >
                <option value="">Not set</option>
                {DATA_LANE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </Card>

          {/* GH #2920. Read-only. These three publish by reference from the
              effective version; this screen edits none of them, and whether it
              should own their editing is an operator decision. */}
          <Card className="p-6">
            <Heading level={2} size="card" className="font-sans mb-2">Carried Parameters</Heading>
            <BodyText variant="ui" className="text-sm text-secondary mb-4">
              Your institution&apos;s workout, line-terms and standby-line settings. This page shows
              them and does not edit them; every version you publish here carries them forward
              unchanged. The scorecard does not apply them today.
            </BodyText>
            <div className="space-y-6">
              <CarriedDialBlock property="workoutOffer" value={carriedDials.workoutOffer} />
              <CarriedDialBlock property="lineTerms" value={carriedDials.lineTerms} />
              <CarriedDialBlock property="standbyTargeting" value={carriedDials.standbyTargeting} />
            </div>
          </Card>

          {/* OC-3b: server-computed what-if -- the form dials (or the
              effective version) evaluate through the production scorecard;
              buildInput maps the form exactly as the publish path does. */}
          <LimitsDistributionPanel
            getCandidateParameters={() => buildInput()?.parameters ?? null}
            dialsKey={dialsKey}
          />

          {localError && (
            <Alert type="warning" title="Check the form">
              {localError}
            </Alert>
          )}

          {publishError && (
            <Alert
              type="error"
              title={
                publishError.code === PUBLISHED_FOR_OTHER_TENANT
                  ? 'Published for a different institution'
                  : 'Publish rejected'
              }
            >
              <div className="space-y-1">
                <div>{publishError.message}</div>
                {publishError.details && publishError.details.length > 0 && (
                  <ul className="list-disc pl-5">
                    {publishError.details.map((detail, index) => (
                      <li key={index}>
                        {detail.issue ?? detail.message ?? 'Validation failure'}
                        {detail.path ? ` (${detail.path})` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Alert>
          )}

          {publishedVersion !== null && (
            <Alert type="success" title="Version published">
              Version {publishedVersion} is now the effective Aaim Tempo coverage policy.
            </Alert>
          )}

          {deliveryModeSection}

          {/*
            Publishing is blocked while the live mode is unreadable, for the same
            reason the mode buttons above are. The server derives the version's
            deliveryMode stamp from Institution.tempoDeliveryMode at publish
            time, and that stamp is IMMUTABLE examiner evidence -- so a publish
            issued from a screen that could not establish the mode writes an
            audit fact nobody on this side can verify. The dials are unaffected
            and stay editable; only the write is held.
          */}
          <div className="flex flex-col items-end gap-2 pt-4 border-t border-recessed">
            {modeUnreadable && (
              <BodyText variant="ui" className="text-xs text-tertiary">
                Publishing is unavailable until the delivery mode can be read.
              </BodyText>
            )}
            <div className="flex items-center justify-end gap-4">
              {confirming ? (
                <div className="flex items-center gap-3">
                  <BodyText variant="ui" className="text-sm text-secondary">
                    Publish immutable version {(policy?.policyVersion ?? 0) + 1}?
                  </BodyText>
                  <Button variant="secondary" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirmPublish}
                    disabled={publishMutation.isPending || !liveMode}
                  >
                    {publishMutation.isPending ? 'Publishing...' : 'Confirm publish'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="primary"
                  onClick={handlePublishClick}
                  disabled={publishMutation.isPending || !liveMode}
                >
                  Publish new version
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
